/**
 * BrandFigure — real photography (no vector illustrations).
 *
 * Each figure kind maps to a licensed-style stock photograph delivered from the
 * CDN as AVIF/WebP with a JPEG fallback and responsive srcset. The public API is
 * unchanged so every existing call site keeps working.
 */

import * as React from "react";
import aiAnswer640Avif from "@/assets/stock/ai-answer-640.avif.asset.json";
import aiAnswer1280Avif from "@/assets/stock/ai-answer-1280.avif.asset.json";
import aiAnswer640Webp from "@/assets/stock/ai-answer-640.webp.asset.json";
import aiAnswer1280Webp from "@/assets/stock/ai-answer-1280.webp.asset.json";
import aiAnswerJpg from "@/assets/stock/ai-answer.jpg.asset.json";

import auditGrid640Avif from "@/assets/stock/audit-grid-640.avif.asset.json";
import auditGrid1280Avif from "@/assets/stock/audit-grid-1280.avif.asset.json";
import auditGrid640Webp from "@/assets/stock/audit-grid-640.webp.asset.json";
import auditGrid1280Webp from "@/assets/stock/audit-grid-1280.webp.asset.json";
import auditGridJpg from "@/assets/stock/audit-grid.jpg.asset.json";

import coverage640Avif from "@/assets/stock/coverage-map-640.avif.asset.json";
import coverage1280Avif from "@/assets/stock/coverage-map-1280.avif.asset.json";
import coverage640Webp from "@/assets/stock/coverage-map-640.webp.asset.json";
import coverage1280Webp from "@/assets/stock/coverage-map-1280.webp.asset.json";
import coverageJpg from "@/assets/stock/coverage-map.jpg.asset.json";

import funnel640Avif from "@/assets/stock/funnel-640.avif.asset.json";
import funnel1280Avif from "@/assets/stock/funnel-1280.avif.asset.json";
import funnel640Webp from "@/assets/stock/funnel-640.webp.asset.json";
import funnel1280Webp from "@/assets/stock/funnel-1280.webp.asset.json";
import funnelJpg from "@/assets/stock/funnel.jpg.asset.json";

import linkWeb640Avif from "@/assets/stock/link-web-640.avif.asset.json";
import linkWeb1280Avif from "@/assets/stock/link-web-1280.avif.asset.json";
import linkWeb640Webp from "@/assets/stock/link-web-640.webp.asset.json";
import linkWeb1280Webp from "@/assets/stock/link-web-1280.webp.asset.json";
import linkWebJpg from "@/assets/stock/link-web.jpg.asset.json";

import mapPack640Avif from "@/assets/stock/map-pack-640.avif.asset.json";
import mapPack1280Avif from "@/assets/stock/map-pack-1280.avif.asset.json";
import mapPack640Webp from "@/assets/stock/map-pack-640.webp.asset.json";
import mapPack1280Webp from "@/assets/stock/map-pack-1280.webp.asset.json";
import mapPackJpg from "@/assets/stock/map-pack.jpg.asset.json";

import rank640Avif from "@/assets/stock/rank-curve-640.avif.asset.json";
import rank1280Avif from "@/assets/stock/rank-curve-1280.avif.asset.json";
import rank640Webp from "@/assets/stock/rank-curve-640.webp.asset.json";
import rank1280Webp from "@/assets/stock/rank-curve-1280.webp.asset.json";
import rankJpg from "@/assets/stock/rank-curve.jpg.asset.json";

import schema640Avif from "@/assets/stock/schema-tree-640.avif.asset.json";
import schema1280Avif from "@/assets/stock/schema-tree-1280.avif.asset.json";
import schema640Webp from "@/assets/stock/schema-tree-640.webp.asset.json";
import schema1280Webp from "@/assets/stock/schema-tree-1280.webp.asset.json";
import schemaJpg from "@/assets/stock/schema-tree.jpg.asset.json";

import cluster640Avif from "@/assets/stock/topic-cluster-640.avif.asset.json";
import cluster1280Avif from "@/assets/stock/topic-cluster-1280.avif.asset.json";
import cluster640Webp from "@/assets/stock/topic-cluster-640.webp.asset.json";
import cluster1280Webp from "@/assets/stock/topic-cluster-1280.webp.asset.json";
import clusterJpg from "@/assets/stock/topic-cluster.jpg.asset.json";

import velocity640Avif from "@/assets/stock/velocity-640.avif.asset.json";
import velocity1280Avif from "@/assets/stock/velocity-1280.avif.asset.json";
import velocity640Webp from "@/assets/stock/velocity-640.webp.asset.json";
import velocity1280Webp from "@/assets/stock/velocity-1280.webp.asset.json";
import velocityJpg from "@/assets/stock/velocity.jpg.asset.json";

type FigureKind =
  | "rank-curve"
  | "map-pack"
  | "schema-tree"
  | "ai-answer"
  | "funnel"
  | "audit-grid"
  | "link-web"
  | "topic-cluster"
  | "velocity"
  | "coverage-map";

type Props = {
  kind: FigureKind;
  title: string;
  className?: string;
};

type Photo = {
  avif640: string;
  avif1280: string;
  webp640: string;
  webp1280: string;
  jpg: string;
  alt: string;
};

const REGISTRY: Record<FigureKind, Photo> = {
  "rank-curve": {
    avif640: rank640Avif.url,
    avif1280: rank1280Avif.url,
    webp640: rank640Webp.url,
    webp1280: rank1280Webp.url,
    jpg: rankJpg.url,
    alt: "SEO analyst in Austin reviewing a rising organic ranking dashboard with keyword-focused data visualization",
  },
  "map-pack": {
    avif640: mapPack640Avif.url,
    avif1280: mapPack1280Avif.url,
    webp640: mapPack640Webp.url,
    webp1280: mapPack1280Webp.url,
    jpg: mapPackJpg.url,
    alt: "Local SEO results on a mobile phone in downtown Austin showing high-intent business map pins",
  },
  "schema-tree": {
    avif640: schema640Avif.url,
    avif1280: schema1280Avif.url,
    webp640: schema640Webp.url,
    webp1280: schema1280Webp.url,
    jpg: schemaJpg.url,
    alt: "Technical SEO engineer implementing structured data and schema markup for an Austin digital marketing client",
  },
  "ai-answer": {
    avif640: aiAnswer640Avif.url,
    avif1280: aiAnswer1280Avif.url,
    webp640: aiAnswer640Webp.url,
    webp1280: aiAnswer1280Webp.url,
    jpg: aiAnswerJpg.url,
    alt: "Austin marketing professional using AI search tools like ChatGPT and Perplexity for keyword research",
  },
  funnel: {
    avif640: funnel640Avif.url,
    avif1280: funnel1280Avif.url,
    webp640: funnel640Webp.url,
    webp1280: funnel1280Webp.url,
    jpg: funnelJpg.url,
    alt: "Austin SEO agency team analyzing a high-converting marketing funnel for a Texas business",
  },
  "audit-grid": {
    avif640: auditGrid640Avif.url,
    avif1280: auditGrid1280Avif.url,
    webp640: auditGrid640Webp.url,
    webp1280: auditGrid1280Webp.url,
    jpg: auditGridJpg.url,
    alt: "Technical SEO specialist in Austin auditing a local business website for Core Web Vitals and site speed",
  },
  "link-web": {
    avif640: linkWeb640Avif.url,
    avif1280: linkWeb1280Avif.url,
    webp640: linkWeb640Webp.url,
    webp1280: linkWeb1280Webp.url,
    jpg: linkWebJpg.url,
    alt: "Austin digital marketing partners shaking hands after a successful white-hat link building campaign",
  },
  "topic-cluster": {
    avif640: cluster640Avif.url,
    avif1280: cluster1280Avif.url,
    webp640: cluster640Webp.url,
    webp1280: cluster1280Webp.url,
    jpg: clusterJpg.url,
    alt: "Content strategist in Austin mapping out SEO topic clusters and topical authority pillars",
  },
  velocity: {
    avif640: velocity640Avif.url,
    avif1280: velocity1280Avif.url,
    webp640: velocity640Webp.url,
    webp1280: velocity1280Webp.url,
    jpg: velocityJpg.url,
    alt: "Laptop screen displaying a real-time Core Web Vitals report and technical SEO monitoring dashboard",
  },
  "coverage-map": {
    avif640: coverage640Avif.url,
    avif1280: coverage1280Avif.url,
    webp640: coverage640Webp.url,
    webp1280: coverage1280Webp.url,
    jpg: coverageJpg.url,
    alt: "Aerial view of Austin, Texas skyline where Optimize Plus provides local SEO and digital marketing services",
  },
};

export function BrandFigure({ kind, title, className }: Props) {
  const photo = REGISTRY[kind] ?? REGISTRY["rank-curve"];
  const [loaded, setLoaded] = React.useState(false);

  return (
    <figure
      className={`overflow-hidden rounded-2xl border border-border bg-surface/60 ${className ?? ""}`}
    >
      <div className="relative aspect-[1280/768] overflow-hidden bg-surface-2">
        {/* Skeleton shown until main image loads */}
        {!loaded && (
          <div
            aria-hidden="true"
            className="absolute inset-0 animate-pulse bg-primary/5"
          />
        )}

        <picture>
          <source
            type="image/avif"
            srcSet={`${photo.avif640} 640w, ${photo.avif1280} 1280w`}
            sizes="(max-width: 768px) 100vw, 640px"
          />
          <source
            type="image/webp"
            srcSet={`${photo.webp640} 640w, ${photo.webp1280} 1280w`}
            sizes="(max-width: 768px) 100vw, 640px"
          />
          <img
            src={photo.jpg}
            alt=""
            width={1280}
            height={768}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => {
              console.error(`[BrandFigure] Failed to load image: ${photo.jpg}`);
              setLoaded(true);
            }}
            className={`h-auto w-full object-cover transition-opacity duration-500 will-change-opacity ${
              loaded ? "opacity-100" : "opacity-0"
            }`}
          />
        </picture>
      </div>
      <figcaption className="sr-only">
        {title}
      </figcaption>
    </figure>
  );
}

/** Deterministic figure choice so a given slug always renders a unique set. */
export function figuresForSlug(slug: string, count = 3): FigureKind[] {
  const kinds: FigureKind[] = [
    "rank-curve",
    "map-pack",
    "schema-tree",
    "ai-answer",
    "funnel",
    "audit-grid",
    "link-web",
    "topic-cluster",
    "velocity",
    "coverage-map"
  ];
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  
  const out: FigureKind[] = [];
  const startIdx = h % kinds.length;
  for (let i = 0; i < count; i++) {
    out.push(kinds[(startIdx + i) % kinds.length]);
  }
  return out;
}

